import { useEffect, useMemo, useRef } from 'react'
import useMosInfo from '../../../common/provider/useMosInfo'
import { getEffectiveVisibleTabs, isDefaultVisibleTabs } from '../utils/plannedJobTabs'

/**
 * Shares the modal's visible tabs between every widget that opens PlannedJobCustomModal -- today
 * Planned Jobs and Operation -- exactly as `globalData.plannedJobListHiddenColumns` already shares
 * the column visibility.
 *
 * The decision is of the WORK CENTRE, not of the widget instance: both widgets mount the same
 * configuration panel and open the same modal, so hiding a tab from one of them has to hide it from
 * the other. Before this, each one kept its own `data.visibleTabs` and the two screens drifted
 * apart.
 *
 * The shared value lives in the MOS context and is mirrored back into each widget's own `data`,
 * which is what actually persists in widgets/device/{id}.
 */
const useSharedVisibleTabs = (data, setData) => {
  const { globalData, setGlobalData } = useMosInfo()
  const sharedVisibleTabsInitRef = useRef(false)

  // Seeds the shared value from this widget's persisted config. A CUSTOMISED list always beats a
  // default one whatever the mount order of the two widgets, so an Operation widget still on the
  // 3-tab retrocompatible default (RN10) cannot erase the tabs the responsible configured on
  // Planned Jobs. If both carry a customised list and they differ, the first to mount wins -- there
  // is no better answer, and the next toggle settles it for both.
  useEffect(() => {
    if (sharedVisibleTabsInitRef.current) return
    sharedVisibleTabsInitRef.current = true
    const own = getEffectiveVisibleTabs(data?.visibleTabs)
    setGlobalData((prev) => {
      const shared = prev?.plannedJobListVisibleTabs
      if (!Array.isArray(shared)) return { ...prev, plannedJobListVisibleTabs: own }
      if (isDefaultVisibleTabs(shared) && !isDefaultVisibleTabs(own)) {
        return { ...prev, plannedJobListVisibleTabs: own }
      }
      return prev
    })
  }, [setGlobalData, data])

  // Mirrors the shared value back into this widget's own config, so the choice taken on the other
  // widget survives a reload instead of living only for the session.
  useEffect(() => {
    const shared = globalData?.plannedJobListVisibleTabs
    if (!Array.isArray(shared)) return
    const current = Array.isArray(data?.visibleTabs) ? data.visibleTabs : []
    if (shared.length === current.length && shared.every((key, index) => key === current[index])) return
    if (setData) setData({ ...(data || {}), visibleTabs: shared })
  }, [globalData?.plannedJobListVisibleTabs, data, setData])

  // Memoized on the CONTENT of the list, never on its identity: getEffectiveVisibleTabs returns a
  // fresh array from `.filter()` on every call, and a new identity on every polling tick resets the
  // modal's selected tab downstream (see PlannedJobCustomModal's activeTabsKey fix).
  const visibleTabsSource = Array.isArray(globalData?.plannedJobListVisibleTabs)
    ? globalData.plannedJobListVisibleTabs
    : data?.visibleTabs
  const visibleTabsContentKey = Array.isArray(visibleTabsSource) ? visibleTabsSource.join('|') : ''
  return useMemo(
    () => getEffectiveVisibleTabs(visibleTabsContentKey === '' ? undefined : visibleTabsContentKey.split('|')),
    [visibleTabsContentKey]
  )
}

export default useSharedVisibleTabs
